import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as interactionAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/interactionAnalyticsActionCreators';
import * as vehicleProtectionActionCreators from '@makemydeal/dr-dash-store/actions/vehicleProtectionActionCreators';
import * as vehicleProtectionSelectors from '@makemydeal/dr-dash-store/selectors/vehicleProtectionSelectors';
import { revalidateSaveStateIfNeeded } from '@makemydeal/dr-dash-store/actions/saveValidationActionCreators';
import type { DraftDealProduct } from '@makemydeal/dr-dash-types';
import type { ManualProduct } from '@makemydeal/dr-shared-types';
import { logManualVppEvent, ManualVppEventName } from '../manualVppTable/newRelicManualVpp';
import { nanoid } from 'nanoid';
import debounce from 'lodash.debounce';

const PAYMENT_DEBOUNCE_TIMER = 2 * 1000;

const initializeSelectedManualProducts = (products: DraftDealProduct[]) => {
    return products.map((product) => ({
        ...product,
        id: (product as ManualProduct).id ?? nanoid(),
        isValid: true
    })) as ManualProduct[];
};

const FIELDS_TO_COMPARE: (keyof DraftDealProduct)[] = [
    'productName',
    'productCategoryCode',
    'productCost',
    'productPrice',
    'profitLocation'
];

export const areProductsEqual = (lastUpdatedProducts: DraftDealProduct[], selectedProducts: DraftDealProduct[]): boolean => {
    if (lastUpdatedProducts.length !== selectedProducts.length) {
        return false;
    }

    return lastUpdatedProducts.every((dispatchedProduct, index) => {
        const storeProduct = selectedProducts[index];

        return FIELDS_TO_COMPARE.every((field) => dispatchedProduct[field] === storeProduct[field]);
    });
};

export const MANUAL_PRODUCT_CODE_SEPARATOR = '_';

/**
 * The Manual VPP "Code" field is optional on the entry UI, but downstream identity (id/menuItemId)
 * and the Shopper Platform mv-only path require a UNIQUE productCode. Generate one at creation for
 * any valid, code-less manual product as `${productCategoryCode}_${index}`.
 *
 * The index is `max(existing index for that category) + 1`, NOT a raw count (a count would collide
 * after a removal: GAP_0, GAP_1 → remove GAP_0 → add → count 1 → GAP_1). The high-water mark is
 * seeded by PARSING the code string itself, independent of the row's current category — so a code
 * that has gone stale (e.g. a `BUNDLE_1` left on a row whose Type was later changed to APAY) still
 * counts toward its original category and its index is never reissued. Without this, two rows could
 * end up sharing a productCode, which breaks identity/dedup and leaves the form permanently dirty.
 * Dealer-entered codes and previously generated codes are preserved as-is, so the value is stable
 * across save/reload and the MV↔SP round-trip and is never re-derived on read.
 */
export const ensureManualProductCodes = (products: ManualProduct[]): ManualProduct[] => {
    const maxIndexByCategory: Record<string, number> = {};

    // Seed each category's high-water mark from every existing `${category}_${index}` code, parsed
    // from the code string (not the row's current category) so a stale code on a retyped row still
    // reserves its index and can never be reissued to another row.
    products.forEach(({ productCode }) => {
        if (!productCode) return;
        const separatorIndex = productCode.lastIndexOf(MANUAL_PRODUCT_CODE_SEPARATOR);
        if (separatorIndex <= 0) return;
        const category = productCode.slice(0, separatorIndex);
        const index = Number(productCode.slice(separatorIndex + 1));
        if (!Number.isInteger(index) || index < 0) return;
        maxIndexByCategory[category] = Math.max(maxIndexByCategory[category] ?? -1, index);
    });

    return products.map((product) => {
        // Preserve any existing code; only assign to a valid product that has a type but no code.
        if (product.productCode || !product.productCategoryCode || !product.isValid) {
            return product;
        }
        const nextIndex = (maxIndexByCategory[product.productCategoryCode] ?? -1) + 1;
        maxIndexByCategory[product.productCategoryCode] = nextIndex;
        return {
            ...product,
            productCode: `${product.productCategoryCode}${MANUAL_PRODUCT_CODE_SEPARATOR}${nextIndex}`
        };
    });
};

export const useManualVpp = (onDirtyCheck?: (checker: () => boolean) => void) => {
    const dispatch = useDispatch();
    const selectedManualProducts = useSelector(vehicleProtectionSelectors.getSelectedManualVppProducts);

    const [internalManualProducts, setInternalManualProducts] = useState<ManualProduct[]>(
        initializeSelectedManualProducts(selectedManualProducts)
    );

    const isManualVppOpened = internalManualProducts.length > 0;

    const lastDispatchedProducts = useRef<DraftDealProduct[]>(selectedManualProducts);

    useEffect(() => {
        const isExternalChange = !areProductsEqual(lastDispatchedProducts.current, selectedManualProducts);

        if (isExternalChange) {
            setInternalManualProducts(initializeSelectedManualProducts(selectedManualProducts));
        }
    }, [selectedManualProducts]);

    // Track form dirty state by comparing current manual products with Redux state
    useEffect(() => {
        if (onDirtyCheck) {
            const hasDataChanges = !areProductsEqual(internalManualProducts, selectedManualProducts);
            const hasValidationErrors = internalManualProducts.some((product) => !product.isValid);
            onDirtyCheck(() => hasDataChanges || hasValidationErrors);
        }
    }, [internalManualProducts, selectedManualProducts, onDirtyCheck]);

    const calculateProductPayments = useCallback(
        debounce(() => dispatch(vehicleProtectionActionCreators.recalculateProductPayments()), PAYMENT_DEBOUNCE_TIMER),
        []
    );

    const setValidManualProducts = (products: ManualProduct[], skipPayment = false): ManualProduct[] => {
        // Generate a productCode for any valid, code-less manual product at this finalize/persist
        // point (see ensureManualProductCodes). The coded list is returned so callers can persist it
        // to internal state — keeping the generated code stable and never re-derived on later edits.
        const productsWithCodes = ensureManualProductCodes(products);

        const validProducts = productsWithCodes.filter((product) => product.isValid);
        const cleanedProducts: DraftDealProduct[] = validProducts.map((product) => {
            const { id: _id, isValid: _isValid, ...cleanedProduct } = product;
            return cleanedProduct;
        });

        productsWithCodes.forEach((product) => {
            logManualVppEvent(ManualVppEventName.PRODUCT_VALIDATED, {
                productName: product.productName,
                productType: product.productCategoryCode,
                isValid: product.isValid || false
            });
        });

        logManualVppEvent(ManualVppEventName.PRODUCTS_SET, {
            productsCount: cleanedProducts.length
        });

        lastDispatchedProducts.current = cleanedProducts;
        dispatch(vehicleProtectionActionCreators.setManualVppProducts(cleanedProducts));
        // A failed save keeps the deal strict until validation passes again.
        dispatch(revalidateSaveStateIfNeeded());

        if (!skipPayment) {
            calculateProductPayments();
        }

        return productsWithCodes;
    };

    const addEmptyInternalManualProduct = () => {
        logManualVppEvent(ManualVppEventName.CTA_CLICKED, {
            ctaType: 'Add Manually'
        });

        dispatch(interactionAnalyticsActionCreators.openFlexibleProtectionProductsEntryLink());
        const emptyProduct = {
            id: nanoid(),
            profitLocation: 'Front',
            productCost: 0,
            productPrice: 0,
            selected: true
        } as ManualProduct;

        setInternalManualProducts([emptyProduct, ...internalManualProducts]);
        return emptyProduct.id;
    };

    const removeManualProduct = (id: string) => {
        dispatch(interactionAnalyticsActionCreators.removeManuallyAddedProtectionProductIcon());
        setInternalManualProducts((internalManualProducts) => {
            const productToDelete = internalManualProducts.find((product) => product.id === id);

            if (productToDelete) {
                logManualVppEvent(ManualVppEventName.PRODUCT_DELETED, {
                    productName: productToDelete.productName,
                    productType: productToDelete.productCategoryCode,
                    productCost: productToDelete.productCost,
                    productPrice: productToDelete.productPrice
                });
            }

            const updatedManualProducts = internalManualProducts.filter((product) => product.id !== id);
            return setValidManualProducts(updatedManualProducts);
        });
    };

    const onUpdateManualProductSingleInput = (value: string | number | undefined, productId: string, inputName: string) => {
        setInternalManualProducts((internalManualProducts) => {
            const updatedManualProducts = internalManualProducts.map((product) =>
                product.id === productId ? { ...product, [inputName]: value } : product
            );
            const productToUpdate = internalManualProducts.find((product) => product.id === productId);

            logManualVppEvent(ManualVppEventName.PRODUCT_EDITED, {
                productName: productToUpdate?.productName,
                inputField: inputName,
                newValue: value
            });

            return updatedManualProducts;
        });
    };

    const onChangeManualProduct = (updatedProduct: ManualProduct, applyProducts?: boolean, skipPayment?: boolean) => {
        setInternalManualProducts((internalManualProducts) => {
            const updatedManualProducts = internalManualProducts.map((product) =>
                product.id === updatedProduct.id ? updatedProduct : product
            );

            if (applyProducts) {
                return setValidManualProducts(updatedManualProducts, skipPayment);
            }

            return updatedManualProducts;
        });
    };

    return {
        isManualVppOpened,
        internalManualProducts,
        addEmptyInternalManualProduct,
        removeManualProduct,
        onUpdateManualProductSingleInput,
        onChangeManualProduct
    };
};
